import React, { createContext, useCallback, useContext, useRef } from "react";
import { useSyncExternalStore } from "use-sync-external-store/shim";

type HelpContent = React.ReactNode | null;

type HelpStore = {
  get: () => HelpContent;
  set: (node: HelpContent) => void;
  subscribe: (cb: () => void) => () => void;
};

function createHelpStore(): HelpStore {
  let current: HelpContent = null;
  const subs = new Set<() => void>();
  return {
    get: () => current,
    set: (node) => {
      current = node;
      subs.forEach((s) => s());
    },
    subscribe: (cb) => {
      subs.add(cb);
      return () => subs.delete(cb);
    },
  };
}

const Ctx = createContext<HelpStore | null>(null);

export function HelpProvider({ children }: { children: React.ReactNode }) {
  const storeRef = useRef<HelpStore>();
  if (!storeRef.current) storeRef.current = createHelpStore();
  return <Ctx.Provider value={storeRef.current}>{children}</Ctx.Provider>;
}

export function useHelp() {
  const store = useContext(Ctx);
  if (!store) throw new Error("useHelp must be used within <HelpProvider>");
  const content = useSyncExternalStore(store.subscribe, store.get, store.get);

  const setHelp = useCallback((node: HelpContent) => store.set(node), [store]);
  const clearHelp = useCallback(() => store.set(null), [store]);

  return { content, setHelp, clearHelp };
}
